import { channelOverridePermissions } from '@/lib/permissions';

/**
 * Server-settings routes for `?mock=full`: roles and channel permission
 * overrides. Mutable, and faithful to the server's rules (trimmed name,
 * 250-role cap, max+1 position, 404 for a vanished role, permanent
 * @everyone, per-channel-type override bits) so the roles and channel
 * permission screens can be designed and checked without a backend. Before
 * this, every write answered `{ ok: true }` and nothing changed on screen.
 */

export interface MockRole {
  id: string;
  guildId: string;
  name: string;
  color: number;
  position: number;
  permissions: string;
  isEveryone?: boolean;
}

export interface MockOverride {
  channelId: string;
  roleId: string;
  allow: string;
  deny: string;
}

export interface MockReply {
  status: number;
  body?: unknown;
}

interface MockChannelInfo {
  guildId: string;
  type: string;
  tournament?: boolean;
}

const ROLE_LIMIT = 250;
const NAME_MAX = 100;

const rolesPath = /^\/api\/guilds\/([^/]+)\/roles$/;
const rolePath = /^\/api\/guilds\/([^/]+)\/roles\/([^/]+)$/;
const reorderPath = /^\/api\/guilds\/([^/]+)\/roles\/reorder$/;
const overridePath = /^\/api\/guilds\/([^/]+)\/channels\/([^/]+)\/overrides(?:\/([^/]+))?$/;

function fail(status: number, error: string): MockReply {
  return { status, body: { error } };
}

type Body = Partial<MockRole> &
  Partial<Pick<MockOverride, 'roleId' | 'allow' | 'deny'>> & {
    orders?: Array<{ id: string; position: number }>;
  };

export function createMockGuildSettings(opts: {
  roles: MockRole[];
  channel: (channelId: string) => MockChannelInfo | undefined;
}) {
  let roles = opts.roles.map((role) => ({ ...role }));
  let overrides: MockOverride[] = [];
  let seq = 0;

  function forGuild(guildId: string) {
    return roles.filter((role) => role.guildId === guildId);
  }

  function createRole(guildId: string, input: Body): MockReply {
    const name = typeof input.name === 'string' ? input.name.trim() : '';
    if (!name || name.length > NAME_MAX) return fail(400, 'validation_error');
    const existing = forGuild(guildId);
    if (existing.length >= ROLE_LIMIT) return fail(429, 'role_limit_reached');
    const role: MockRole = {
      id: `r_mock_${++seq}`,
      guildId,
      name,
      color: input.color ?? 0,
      permissions: input.permissions ?? '0',
      position: input.position ?? Math.max(0, ...existing.map((r) => r.position)) + 1,
    };
    roles = [...roles, role];
    return { status: 201, body: { ...role } };
  }

  function updateRole(guildId: string, roleId: string, input: Body): MockReply {
    const existing = roles.find((role) => role.id === roleId && role.guildId === guildId);
    if (!existing) return fail(404, 'role_not_found');
    if (roleId === guildId && (input.name !== undefined || input.color !== undefined || input.position !== undefined)) {
      return fail(400, 'everyone_role_locked');
    }
    const name = input.name === undefined ? undefined : String(input.name).trim();
    if (name !== undefined && (!name || name.length > NAME_MAX)) return fail(400, 'validation_error');
    const next: MockRole = {
      ...existing,
      ...(name !== undefined ? { name } : {}),
      ...(input.color !== undefined ? { color: input.color } : {}),
      ...(input.permissions !== undefined ? { permissions: input.permissions } : {}),
      ...(input.position !== undefined ? { position: input.position } : {}),
    };
    roles = roles.map((role) => (role.id === roleId ? next : role));
    return { status: 200, body: { ...next } };
  }

  function deleteRole(guildId: string, roleId: string): MockReply {
    if (roleId === guildId) return fail(403, 'role_hierarchy_forbidden');
    if (!roles.some((role) => role.id === roleId && role.guildId === guildId)) {
      return fail(404, 'role_not_found');
    }
    roles = roles.filter((role) => role.id !== roleId);
    overrides = overrides.filter((row) => row.roleId !== roleId);
    return { status: 204 };
  }

  function reorderRoles(guildId: string, orders: Array<{ id: string; position: number }>): MockReply {
    if (orders.some((order) => order.id === guildId)) return fail(400, 'everyone_role_locked');
    const ids = new Set(forGuild(guildId).map((role) => role.id));
    if (!orders.every((order) => ids.has(order.id))) return fail(404, 'role_not_found');
    const byId = new Map(orders.map((order) => [order.id, order.position]));
    roles = roles.map((role) => (byId.has(role.id) ? { ...role, position: byId.get(role.id)! } : role));
    return { status: 204 };
  }

  function upsertOverride(guildId: string, channelId: string, input: Body): MockReply {
    const channel = opts.channel(channelId);
    if (!channel || channel.guildId !== guildId) return fail(404, 'channel_not_found');
    const roleId = input.roleId ?? '';
    if (!roles.some((role) => role.id === roleId && role.guildId === guildId)) return fail(400, 'invalid_role');
    const allow = BigInt(input.allow ?? '0');
    const deny = BigInt(input.deny ?? '0');
    if (((allow | deny) & ~channelOverridePermissions(channel)) !== 0n || (allow & deny) !== 0n) {
      return fail(400, 'permission_not_applicable');
    }
    const row: MockOverride = { channelId, roleId, allow: allow.toString(), deny: deny.toString() };
    overrides = [...overrides.filter((item) => !(item.channelId === channelId && item.roleId === roleId)), row];
    return { status: 200, body: { ...row } };
  }

  function deleteOverride(channelId: string, roleId: string): MockReply {
    overrides = overrides.filter((item) => !(item.channelId === channelId && item.roleId === roleId));
    return { status: 204 };
  }

  return {
    /** Current roles of a guild, as `GET /api/guilds/:id` returns them. */
    roles(guildId: string): MockRole[] {
      return forGuild(guildId).map((role) => ({ ...role }));
    },
    /** Overrides stored for a channel, as the guild detail embeds them. */
    overrides(channelId: string): MockOverride[] {
      return overrides.filter((row) => row.channelId === channelId).map((row) => ({ ...row }));
    },
    /** A reply for a settings route, or `null` when the path is not one. */
    handle(path: string, method: string, body: unknown): MockReply | null {
      const input = (body ?? {}) as Body;
      const reorderMatch = path.match(reorderPath);
      if (reorderMatch && method === 'POST') return reorderRoles(reorderMatch[1]!, input.orders ?? []);
      const listMatch = path.match(rolesPath);
      if (listMatch && method === 'POST') return createRole(listMatch[1]!, input);
      const roleMatch = path.match(rolePath);
      if (roleMatch && method === 'PATCH') return updateRole(roleMatch[1]!, roleMatch[2]!, input);
      if (roleMatch && method === 'DELETE') return deleteRole(roleMatch[1]!, roleMatch[2]!);
      const overrideMatch = path.match(overridePath);
      if (overrideMatch && method === 'PUT' && !overrideMatch[3]) {
        return upsertOverride(overrideMatch[1]!, overrideMatch[2]!, input);
      }
      if (overrideMatch && method === 'DELETE' && overrideMatch[3]) {
        return deleteOverride(overrideMatch[2]!, overrideMatch[3]);
      }
      return null;
    },
  };
}
